import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Clock, GraduationCap, X, Eye, Download, FolderOpen, ChevronRight, ChevronDown, FileCheck, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RESOURCE_TYPES, RESOURCE_CONFIG, DEFAULT_SHARIAH_PRODUCTS } from '../config/resourceConfig.js';
import { formatFileSize } from '../utils/fileUtils.js';
import DocumentUploadSection from './DocumentUploadSection.jsx';
import TemplateReviewWizard from '@/components/templates/TemplateReviewWizard.jsx';
import { isTemplateFillOnlineSupported } from '@/utils/templateFillSupport.js';
import { TEMPLATE_FIELD_POLICY } from '@/constants/templateWizardPolicy.js';

const TrainingResourcesModal = ({
  isOpen,
  onClose,
  trainingCompleted,
  trainingProgress,
  resourceFiles,
  loadingResourceFiles,
  onTrainingProgress,
  onCompleteTraining,
  onViewResourceFile,
  onDownloadResourceFile,
  onLoadResourceFiles,
  trainingUploadedFiles = [],
  isTrainingUploading = false,
  trainingUploadProgress = '',
  trainingUploadStatus = '',
  onTrainingFileUpload,
  onViewTrainingFile,
  onDeleteTrainingFile,
  isViewingTrainingFile = false,
  onTrainingTemplateSecureSubmitted
}) => {
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [shariahProducts, setShariahProducts] = useState([...DEFAULT_SHARIAH_PRODUCTS]);
  const [isTemplateWizardOpen, setIsTemplateWizardOpen] = useState(false);
  const [activeTemplateForWizard, setActiveTemplateForWizard] = useState(null);

  // Load resource files when modal opens (only once when modal opens)
  useEffect(() => {
    if (isOpen && onLoadResourceFiles) {
      onLoadResourceFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen, not onLoadResourceFiles to avoid re-running

  // Organize Shariyah Templates by folder/product type
  const organizeShariaFilesByFolder = () => {
    const organized = {};
    const shariaFiles = resourceFiles[RESOURCE_TYPES.SHARIA_TEMPLATES] || [];
    
    // Initialize all product folders
    shariahProducts.forEach(product => {
      organized[product] = [];
    });
    
    // Group files by folder
    shariaFiles.forEach(file => {
      // Extract folder name from S3 key: resources/sharia-templates/{folder}/{filename}
      const parts = file.key.split('/');
      if (parts.length >= 4 && parts[0] === 'resources' && parts[1] === 'sharia-templates') {
        // File is in a folder: resources/sharia-templates/{folder}/{filename}
        const folderName = parts[2];
        if (organized[folderName]) {
          organized[folderName].push(file);
        } else {
          // If folder doesn't exist in current list, add it dynamically
          if (!organized[folderName]) {
            organized[folderName] = [];
          }
          organized[folderName].push(file);
          // Also add to state if not already there
          if (!shariahProducts.includes(folderName)) {
            setShariahProducts(prev => [...prev, folderName]);
          }
        }
      } else if (parts.length === 3 && parts[0] === 'resources' && parts[1] === 'sharia-templates') {
        // Legacy file without folder: resources/sharia-templates/{filename}
        if (!organized['Other']) {
          organized['Other'] = [];
        }
        organized['Other'].push(file);
      } else {
        // Files without proper structure go to "Other"
        if (!organized['Other']) {
          organized['Other'] = [];
        }
        organized['Other'].push(file);
      }
    });
    
    return organized;
  };

  const handleFolderToggle = (folderName) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderName)) {
        newSet.delete(folderName);
      } else {
        newSet.add(folderName);
      }
      return newSet;
    });
  };

  const openTemplateWizard = (file) => {
    if (!file?.url || !file?.name) {
      console.error('[TrainingResourcesModal] template_wizard_missing_file_url', { file });
      return;
    }
    console.info('[TrainingResourcesModal] open_template_wizard', { key: file.key, name: file.name });
    setActiveTemplateForWizard({ name: file.name, url: file.url });
    setIsTemplateWizardOpen(true);
  };

  const closeTemplateWizard = () => {
    setIsTemplateWizardOpen(false);
    setActiveTemplateForWizard(null);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <GraduationCap className="w-6 h-6" />
                  <h2 className="text-xl font-semibold">Product Development</h2>
                </div>
                <button
                  onClick={onClose}
                  className="text-white hover:text-emerald-100 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="space-y-6 text-[#1a4a1a]">
                {/* Status Section */}
                {trainingCompleted ? (
                  <div className="p-4 rounded-lg border bg-green-50 border-green-200">
                    <div className="flex items-center gap-3 mb-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-green-800">Status: Completed</h3>
                    </div>
                    <p className="text-sm text-green-700">
                      Product development completed successfully.
                    </p>
                  </div>
                ) : (
                  <div className="p-4 rounded-lg border-2 bg-sky-50 border-sky-300">
                    <div className="flex items-center gap-3 mb-3">
                      <Clock className="w-5 h-5 text-sky-600" />
                      <h3 className="font-semibold text-sky-900">What to do next</h3>
                    </div>
                    <ol className="text-sm text-sky-900 space-y-3 list-decimal list-inside">
                      <li>
                        <strong>Download</strong> the Shariyah templates (below) for the Islamic financing product you are building.
                      </li>
                      <li>
                        <strong>Complete the template.</strong> Add all required details. You cannot use these templates as-is. Read them, modify them to fit your company policy, and ensure they include everything your institution needs.
                      </li>
                      <li>
                        <strong>Upload</strong> the modified templates in the upload section below.
                      </li>
                    </ol>
                    <p className="text-sm text-sky-800 mt-3">
                      After you upload, our product development team will review your submissions and let you know if any changes are required.
                    </p>
                    <div className="mt-3 pt-3 border-t border-sky-200">
                      <p className="text-sm text-sky-900">
                        <strong>Note:</strong> Refer to the additional resources below (Islamic Financing 101, Sales Training, System Training) for guidance as you work through your product development.
                      </p>
                    </div>
                  </div>
                )}

                {/* Resources Section - Shariyah Templates → Upload → Other Resources */}
                <div>
                  <div className="border-b border-gray-200 pb-4 mb-4">
                    <h3 className="text-lg font-semibold mb-1">Resources</h3>
                    <p className="text-sm text-gray-600">
                      Download templates and reference materials. Start with Shariyah Templates, then use the other resources as needed.
                    </p>
                  </div>
                  
                  {loadingResourceFiles && (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                      <span className="ml-3 text-gray-600">Loading resources...</span>
                    </div>
                  )}

                  {!loadingResourceFiles && (
                    <div className="space-y-6">
                      {/* 1. Shariyah Templates - Download here */}
                      {(() => {
                        const config = RESOURCE_CONFIG[RESOURCE_TYPES.SHARIA_TEMPLATES];
                        const Icon = config.icon;
                        const files = resourceFiles[RESOURCE_TYPES.SHARIA_TEMPLATES] || [];
                        const filesByFolder = organizeShariaFilesByFolder();
                        const totalFiles = files.length;

                        return (
                          <div className="border rounded-lg p-4">
                            <div className="flex items-center gap-3 mb-4">
                              <Icon className="w-5 h-5 text-emerald-600" />
                              <h4 className="font-semibold text-gray-900">{config.label}</h4>
                              <Badge variant="outline" className="ml-auto">{totalFiles} files</Badge>
                            </div>
                            {totalFiles > 0 ? (
                              <div className="space-y-3">
                                {shariahProducts.map((folderName) => {
                                  const folderFiles = filesByFolder[folderName] || [];
                                  const fileCount = folderFiles.length;
                                  const isExpanded = expandedFolders.has(folderName);
                                  if (fileCount === 0) return null;
                                  return (
                                    <div key={folderName} className="border rounded-lg overflow-hidden">
                                      <button
                                        onClick={() => handleFolderToggle(folderName)}
                                        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                      >
                                        <div className="flex items-center gap-3">
                                          <div className="p-1.5 bg-emerald-100 rounded">
                                            <FolderOpen className="w-4 h-4 text-emerald-600" />
                                          </div>
                                          <div className="text-left">
                                            <h5 className="font-medium text-gray-900">{folderName}</h5>
                                            <p className="text-xs text-gray-500">{fileCount} {fileCount === 1 ? 'template' : 'templates'}</p>
                                          </div>
                                        </div>
                                        {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                                      </button>
                                      {isExpanded && (
                                        <div className="p-3 bg-white border-t">
                                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {folderFiles.map((file) => (
                                              <Card key={file.key} className="hover:shadow-md transition-shadow">
                                                <CardContent className="p-3">
                                                  <div className="flex items-start gap-2 mb-2">
                                                    <FileCheck className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                                    <div className="flex-1 min-w-0">
                                                      <p className="font-medium text-sm text-gray-900 truncate">{file.name}</p>
                                                      <p className="text-xs text-gray-500 mt-1">{formatFileSize(file.size)}</p>
                                                    </div>
                                                  </div>
                                                  <div className="flex gap-2">
                                                    <Button size="sm" variant="outline" onClick={() => onViewResourceFile(file)} className="flex-1 text-xs">
                                                      <Eye className="w-3 h-3 mr-1" />View
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => onDownloadResourceFile(file)} className="flex-1 text-xs">
                                                      <Download className="w-3 h-3 mr-1" />Download
                                                    </Button>
                                                  </div>
                                                  {isTemplateFillOnlineSupported(file.name) && (
                                                    <div className="mt-2">
                                                      <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => openTemplateWizard(file)}
                                                        className="w-full text-xs"
                                                      >
                                                        <FileText className="w-3 h-3 mr-1" />
                                                        Fill online
                                                      </Button>
                                                    </div>
                                                  )}
                                                </CardContent>
                                              </Card>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 text-center py-4">{config.emptyStateMessage}</p>
                            )}
                          </div>
                        );
                      })()}

                      {/* 2. Upload - Right after Shariyah Templates */}
                      <DocumentUploadSection
                        title="Upload Your Product Development Documents"
                        description="Upload your customized product development documents. Supported formats: PDF, DOC, DOCX, RTF, XLS, XLSX, ODT, ODS, TXT, CSV, JPG, PNG (Max 10MB each)"
                        acceptedFormats=".pdf,.doc,.docx,.rtf,.xls,.xlsx,.odt,.ods,.txt,.csv,.jpg,.jpeg,.png"
                        uploadedFiles={trainingUploadedFiles}
                        isUploading={isTrainingUploading}
                        uploadProgress={trainingUploadProgress}
                        uploadStatus={trainingUploadStatus}
                        onUpload={onTrainingFileUpload}
                        onViewFile={onViewTrainingFile}
                        onDeleteFile={onDeleteTrainingFile}
                        isViewingFile={isViewingTrainingFile}
                        disabled={trainingCompleted}
                        hideUploadArea={trainingCompleted}
                        uploadInputId="training-document-upload"
                      />

                      {/* 3. Ready to Complete */}
                      {!trainingCompleted && trainingUploadedFiles.length > 0 && (
                        <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                          <h4 className="font-semibold text-emerald-800 mb-2">Ready to Complete</h4>
                          <p className="text-sm text-emerald-700 mb-4">
                            You have uploaded {trainingUploadedFiles.length} document(s). You can now mark this step as complete.
                          </p>
                          <Button onClick={onCompleteTraining} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                            Mark Step Complete
                          </Button>
                        </div>
                      )}

                      {/* 4. Additional Resources */}
                      <div className="border-t border-gray-200 pt-6">
                        <h4 className="text-base font-semibold text-gray-800 mb-4">Additional Resources</h4>
                        <div className="space-y-6">
                          {[RESOURCE_TYPES.ISLAMIC_FINANCE, RESOURCE_TYPES.SALES_TRAINING, RESOURCE_TYPES.SYSTEM_TRAINING, RESOURCE_TYPES.OTHER_RESOURCES].map((resourceType) => {
                            const config = RESOURCE_CONFIG[resourceType];
                            const Icon = config.icon;
                            const files = resourceFiles[resourceType] || [];

                            return (
                              <div key={resourceType} className="border rounded-lg p-4">
                                <div className="flex items-center gap-3 mb-4">
                                  <Icon className="w-5 h-5 text-emerald-600" />
                                  <h4 className="font-semibold text-gray-900">{config.label}</h4>
                                  <Badge variant="outline" className="ml-auto">{files.length} files</Badge>
                                </div>
                                {files.length > 0 ? (
                                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {files.map((file) => (
                                      <Card key={file.key} className="hover:shadow-md transition-shadow">
                                        <CardContent className="p-4">
                                          <div className="flex items-start justify-between mb-3">
                                            <div className="flex-1 min-w-0">
                                              <p className="font-medium text-sm text-gray-900 truncate">{file.name}</p>
                                              <p className="text-xs text-gray-500 mt-1">{formatFileSize(file.size)}</p>
                                            </div>
                                          </div>
                                          <div className="flex gap-2">
                                            <Button size="sm" variant="outline" onClick={() => onViewResourceFile(file)} className="flex-1">
                                              <Eye className="w-3 h-3 mr-1" />View
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => onDownloadResourceFile(file)} className="flex-1">
                                              <Download className="w-3 h-3 mr-1" />Download
                                            </Button>
                                          </div>
                                        </CardContent>
                                      </Card>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500 text-center py-4">{config.emptyStateMessage}</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Template Wizard Overlay (guided fill & download) */}
            <AnimatePresence>
              {isTemplateWizardOpen && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex items-center justify-center p-4"
                  onClick={closeTemplateWizard}
                >
                  <motion.div
                    initial={{ scale: 0.96, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.96, opacity: 0 }}
                    className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[92vh] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold">Fill this template online</h3>
                          <p className="text-sm text-emerald-100">
                            For Islamic Finance and riba-free product teams. Download an updated copy when finished.
                          </p>
                        </div>
                        <button
                          onClick={closeTemplateWizard}
                          className="text-white hover:text-emerald-100 transition-colors"
                          aria-label="Close"
                        >
                          <X className="w-6 h-6" />
                        </button>
                      </div>
                    </div>

                    <div className="p-6 max-h-[76vh] overflow-y-auto">
                      <TemplateReviewWizard
                        fieldPolicy={TEMPLATE_FIELD_POLICY.OPTIONAL}
                        showHeader={false}
                        initialTemplate={activeTemplateForWizard}
                        enableSecureSubmit
                        secureSubmitDocumentType="productDevelopment"
                        onError={(message, err) => {
                          console.error('[TrainingResourcesModal] template_wizard_error', { message, err, activeTemplateForWizard });
                        }}
                        onCompleted={() => {
                          console.info('[TrainingResourcesModal] template_wizard_completed', { activeTemplateForWizard });
                        }}
                        onSecureSubmitted={(result) => {
                          console.info('[TrainingResourcesModal] template_submitted_secure_storage', {
                            activeTemplateForWizard,
                            s3Key: result?.s3Key,
                          });
                          if (typeof onTrainingTemplateSecureSubmitted === 'function') {
                            onTrainingTemplateSecureSubmitted(result);
                          }
                        }}
                      />
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Footer */}
            <div className="bg-gray-50 p-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <Button
                  onClick={onClose}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Close
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TrainingResourcesModal;

